import { z } from 'zod/v4';

import { AssemblyTypeEnumRuntype } from '../assembly';
import {
    MonetaryValueBackend,
    MonetaryValueBackendRuntype,
    QuantityRuntype,
    QuantityUnitDTORuntypeOld,
} from '../backendTypes';
import { SourcingContextRuntype } from '../dataSource/dataSourceBackendTypes';
import { DemandPostDTORuntype } from '../demand/demandBackendTypes';
import { AvailabilityRuntype } from '../offer/availablityBackendTypes';
import { SupplierLocationRuntype } from '../offer/offerBackendTypes';
import { SolutionDTORuntype, TcoCostItemRuntype, TcoCostSummaryRuntype } from '../solution/solutionBackendTypes';
import {
    OfferSelectionMetadataRuntype,
    SourcingLineItemCalculationDTO,
} from '../sourcingLineItem/sourcingLineItemBackendTypes';
import { SolutionPreferenceDTORuntype, SolutionPreferenceInputDTORuntype } from './solutionPreferenceBackendTypes';

/** As Rust's `SOURCING_SCENARIO_NAME_RANGE`; the export's `unique_sheet_name` cuts names to Excel's 30 characters. */
export const SOURCING_SCENARIO_NAME_MAX_LENGTH = 200;

export type AssemblySourcingSummaryDTO = z.infer<typeof AssemblySourcingSummaryRuntype>;
const AssemblySourcingSummaryRuntype = z.object({
    unit_price: MonetaryValueBackendRuntype.nullable(),
    total_tco_costs: TcoCostSummaryRuntype,
    total_price: MonetaryValueBackendRuntype.nullable(),
    total_purchase_price: MonetaryValueBackendRuntype.nullable(),
    total_scrap_costs: MonetaryValueBackendRuntype.nullable(),
    total_excess_material: MonetaryValueBackendRuntype.nullable(),
    total_one_time_costs: MonetaryValueBackendRuntype.nullable(),
    availability: AvailabilityRuntype.nullable(),
    has_missing_solutions: z.boolean(),
});

const DemandAssemblyRuntype = z.object({
    id: z.string(),
    name: z.string(),
    assembly_type: z.object({ type: AssemblyTypeEnumRuntype }),
});

const DemandPartIdRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('OffTheShelf'), data: z.string() }),
    z.object({ type: z.literal('Ipn'), data: z.string() }),
]);

const DemandPartRuntype = z.object({
    id: DemandPartIdRuntype,
});

export const SourcingScenarioDemandKindRuntype = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('Assembly'), data: DemandAssemblyRuntype }),
    z.object({ kind: z.literal('Part'), data: DemandPartRuntype }),
]);
export type SourcingScenarioDemandKind = z.infer<typeof SourcingScenarioDemandKindRuntype>;

export const SourcingScenarioDemandDTORuntype = z.object({
    id: z.string(),
    context: z.string().nullable(),
    quantity: QuantityUnitDTORuntypeOld,
    kind: SourcingScenarioDemandKindRuntype,
    production_start_date: z.string().nullable(),
    delivery_start_date: z.string().nullable(),
    delivery_end_date: z.string().nullable(),
    end_customer: z.string().nullable(),
    created_at: z.string(),
    sourcing_scenario: z.string(),
    ship_to_site: z.string().nullable(),
});
export type SourcingScenarioDemandDTO = z.infer<typeof SourcingScenarioDemandDTORuntype>;

export interface ItemObject extends z.infer<typeof ItemObjectRuntype> {}
const ItemObjectRuntype = z.object({
    assembly: z.string(),
    quantity: z.number(),
});

export interface AssemblyQuantities extends z.infer<typeof AssemblyQuantitiesRuntype> {}
const AssemblyQuantitiesRuntype = z.object({
    items: z.array(ItemObjectRuntype),
});

const SourcingScenarioTypeRuntype = z.enum(['NormalScenario', 'AwardScenario', 'BaseScenario']);
export type SourcingScenarioType = z.infer<typeof SourcingScenarioTypeRuntype>;

export interface SourcingScenarioDTO extends z.infer<typeof SourcingScenarioDTORuntype> {}
export const SourcingScenarioDTORuntype = z.object({
    id: z.string(),
    project: SourcingContextRuntype,
    demand_group_id: z.string(),
    name: z.string(),
    solution_preference: SolutionPreferenceDTORuntype,
    assembly_quantities: AssemblyQuantitiesRuntype, // @deprecated - use assembly_demands instead
    assembly_demands: z.array(SourcingScenarioDemandDTORuntype),
    origin_sourcing_template_id: z.uuid().nullable(),
    sourcing_scenario_type: SourcingScenarioTypeRuntype.optional(),
});

export interface SourcingScenarioPostDTO extends z.infer<typeof SourcingScenarioPostDTORuntype> {}
export const SourcingScenarioPostDTORuntype = z.object({
    project: SourcingContextRuntype,
    name: z.string(),
    solution_preference: SolutionPreferenceInputDTORuntype,
    assembly_demands: z
        .array(
            z.union([
                z.object({
                    kind: z.literal('existing'),
                    data: z.object({ id: z.string() }),
                }),
                z.object({ kind: z.literal('new'), data: DemandPostDTORuntype }),
            ]),
        )
        .optional(),
    demand_group_id: z.string().optional(),
    origin_sourcing_template_id: z.string().uuid().nullable(),
});

export interface SourcingScenarioPatchDTO extends z.infer<typeof SourcingScenarioPatchDTORuntype> {}
export const SourcingScenarioPatchDTORuntype = SourcingScenarioPostDTORuntype.omit({ project: true }).partial();

export interface SupplierSolutionDTO extends z.infer<typeof SupplierSolutionRuntype> {}
export const SupplierSolutionRuntype = z.object({
    linked_location: SupplierLocationRuntype,
    sourcing_scenario_solutions: z.array(
        z.object({
            solutions: z.array(SolutionDTORuntype),
            solution_configuration_id: z.string(),
            sourcing_scenario: SourcingScenarioDTORuntype,
        }),
    ),
});

const ExclusionReasonRuntype = z.union([
    z.literal('DoNotPlace'),
    z.literal('NoPartOptions'),
    z.literal('QuantityMissingInBom'),
    z.literal('QuantityIsZero'),
    z.literal('Internal'),
]);

const ExcludedBomItemRuntype = z.object({
    design_item_ids: z.array(z.string()),
    assembly_id: z.string(),
    reason: ExclusionReasonRuntype,
});

const DesignItemInfoDtoRuntype = z.object({
    id: z.string(),
    assembly: z.string(),
    designator: z.string().nullable(),
    is_consigned: z.boolean(),
});
export type DesignItemInfoDTO = z.infer<typeof DesignItemInfoDtoRuntype>;

const AssemblyInfoDtoRuntype = z.object({
    id: z.string(),
    designator: z.string(),
    /** @deprecated Use the cost breakdown's `parent_assembly_id` / `parent_path`; kept for `extractDesignItems`. */
    paths: z.array(z.array(z.string())),
});

const LineItemsSummaryRuntype = z.object({
    total_line_items: z.number(),
    number_of_error: z.number(),
    number_of_warning: z.number(),
    number_of_ok: z.number(),
    number_of_excluded: z.number(),
    number_not_sourced: z.number(),
    total_availability: AvailabilityRuntype.nullable(),
});
export type LineItemsSummaryDTO = z.infer<typeof LineItemsSummaryRuntype>;

const SourcingScenarioCostRuntype = z.object({
    total_excess_material: MonetaryValueBackendRuntype.nullable(),
    total_price: MonetaryValueBackendRuntype.nullable(),
    total_tco_costs: TcoCostSummaryRuntype,
    total_one_time_costs: MonetaryValueBackendRuntype.nullable(),
    total_unit_price: MonetaryValueBackendRuntype.nullable(),
    total_scrap_costs: MonetaryValueBackendRuntype.nullable(),
    total_purchase_price: MonetaryValueBackendRuntype.nullable(),
});
export type SourcingScenarioCostDTO = z.infer<typeof SourcingScenarioCostRuntype>;

// The descriptive part of a BOM item, shared by every variant of
// DemandPathBomItemRuntype.
export const BomItemInfoRuntype = z.object({
    id: z.string(),
    // Keys of the calculation's `design_items` map.
    design_item_ids: z.array(z.string()),
    designators: z.array(z.string()),
});
export type BomItemInfoDTO = z.infer<typeof BomItemInfoRuntype>;

const BomItemQuantityErrorRuntype = z.enum([
    'QuantityMissingInBom',
    'DesignItemMeasureMismatch',
    'QuantityIsZero',
    'QuantityUnitConversionError',
]);
export type BomItemQuantityErrorDTO = z.infer<typeof BomItemQuantityErrorRuntype>;

export const AggregationExclusionReasonRuntype = z.union([
    z.literal('DoNotPlace'),
    z.literal('NoPartOptions'),
    z.object({ QuantityError: BomItemQuantityErrorRuntype }),
]);
export type AggregationExclusionReasonDTO = z.infer<typeof AggregationExclusionReasonRuntype>;

// This BOM item's cost contribution to a single assembly.
export const BomItemPerAssemblyCostRuntype = z.object({
    // Price excluding excess material.
    price: MonetaryValueBackendRuntype.nullable(),
    // Price including excess material.
    purchase_price: MonetaryValueBackendRuntype.nullable(),
});
export type BomItemPerAssemblyCostDTO = z.infer<typeof BomItemPerAssemblyCostRuntype>;

// This BOM item's cost for this placement's demanded quantity.
export const BomItemTotalCostRuntype = z.object({
    // Price excluding excess material.
    price: MonetaryValueBackendRuntype.nullable(),
    // Price including excess material.
    purchase_price: MonetaryValueBackendRuntype.nullable(),
    scrap_cost: MonetaryValueBackendRuntype.nullable(),
    one_time_cost: MonetaryValueBackendRuntype.nullable(),
    tco_cost: MonetaryValueBackendRuntype.nullable(),
    excess_cost: MonetaryValueBackendRuntype.nullable(),
});
export type BomItemTotalCostDTO = z.infer<typeof BomItemTotalCostRuntype>;

// Costed variant of DemandPathBomItemRuntype.
export const BomItemCostRuntype = z.object({
    kind: z.literal('Costed'),
    bom_item: BomItemInfoRuntype,
    // Id of an entry in the calculation's `line_item_calculations`.
    sourcing_line_item_id: z.string().nullable(),

    per_assembly_quantity: QuantityRuntype,
    total_quantity: QuantityRuntype,

    per_assembly: BomItemPerAssemblyCostRuntype,
    total: BomItemTotalCostRuntype,
});
export type BomItemCostDTO = z.infer<typeof BomItemCostRuntype>;

// NotCosted variant of DemandPathBomItemRuntype.
const NotCostedDemandPathBomItemRuntype = z.object({
    kind: z.literal('NotCosted'),
    bom_item: BomItemInfoRuntype,
    sourcing_line_item_id: z.string().nullable(),
});
export type NotCostedDemandPathBomItemDTO = z.infer<typeof NotCostedDemandPathBomItemRuntype>;

// Excluded variant of DemandPathBomItemRuntype.
const ExcludedDemandPathBomItemRuntype = z.object({
    kind: z.literal('Excluded'),
    bom_item: BomItemInfoRuntype,
    sourcing_line_item_id: z.string().nullable(),
    exclusion_reason: AggregationExclusionReasonRuntype,
});
export type ExcludedDemandPathBomItemDTO = z.infer<typeof ExcludedDemandPathBomItemRuntype>;

// A single BOM item in an assembly cost breakdown, discriminated by `kind`.
export const DemandPathBomItemRuntype = z.discriminatedUnion('kind', [
    BomItemCostRuntype,
    NotCostedDemandPathBomItemRuntype,
    ExcludedDemandPathBomItemRuntype,
]);
export type DemandPathBomItemDTO = z.infer<typeof DemandPathBomItemRuntype>;

/** One assembly placement's costs, as defined on `CostSummaryRuntype` in `solution/solutionBackendTypes.ts`. */
export const AssemblyCostTotalsRuntype = z.object({
    unit_price_total_purchase: MonetaryValueBackendRuntype.nullable(),
    unit_price_total: MonetaryValueBackendRuntype.nullable(),
    /** What is paid at the supplier: `total_price` plus excess. */
    total_purchase_price: MonetaryValueBackendRuntype.nullable(),
    /** TCO, one-time cost and scrap, but no excess: never subtract `total_excess_cost` from it. */
    total_price: MonetaryValueBackendRuntype.nullable(),
    /** No one-time cost and, as `total_price`, no excess. */
    total_price_without_otc: MonetaryValueBackendRuntype.nullable(),
    total_scrap_cost: MonetaryValueBackendRuntype.nullable(),
    total_one_time_cost: MonetaryValueBackendRuntype.nullable(),
    /** TCO on the whole purchase quantity: it counts the TCO in `total_scrap_cost` and `total_excess_cost` again. */
    total_tco_cost: MonetaryValueBackendRuntype.nullable(),
    total_excess_cost: MonetaryValueBackendRuntype.nullable(),
});
export type AssemblyCostTotalsDTO = z.infer<typeof AssemblyCostTotalsRuntype>;

// One step of an assembly's path: id-based placement, readable as a breadcrumb.
export const AssemblyPathEntryRuntype = z.object({
    assembly_id: z.string(),
    designator: z.string(),
});
export type AssemblyPathEntryDTO = z.infer<typeof AssemblyPathEntryRuntype>;

// One per assembly and demand.
export const AssemblyCostBreakdownRuntype = z.object({
    // Key of the calculation's `assemblies` map.
    assembly_id: z.string(),
    // Id of an entry in SourcingScenarioDTO.assembly_demands.
    demand_id: z.string(),
    // Immediate parent's assembly id, or null when this row is the demand's root.
    parent_assembly_id: z.string().nullable(),
    // Chain from the demand root down to the parent (this assembly excluded).
    parent_path: z.array(AssemblyPathEntryRuntype),
    // Depth of this row on its path: 1 at the demand root, equal to parent_path.length + 1.
    level: z.number(),
    assembly_quantity: QuantityRuntype,
    // This assembly alone.
    direct: AssemblyCostTotalsRuntype,
    // This assembly plus all its sub-assemblies.
    rolled_up: AssemblyCostTotalsRuntype,
    bom_items: z.array(DemandPathBomItemRuntype),
});
export type AssemblyCostBreakdownDTO = z.infer<typeof AssemblyCostBreakdownRuntype>;

/** A part demand's share of its solution's costs; see `CostSummaryRuntype` in `solution/solutionBackendTypes.ts`. */
export const SolutionCostShareRuntype = z.object({
    /** What is paid at the supplier: `total_price` plus excess. */
    total_purchase_price: MonetaryValueBackendRuntype.nullable(),
    /** Includes TCO, one-time cost and scrap. **Excludes excess.** */
    total_price: MonetaryValueBackendRuntype.nullable(),
    total_price_without_otc: MonetaryValueBackendRuntype.nullable(),
    total_price_without_otc_and_additional_costs: MonetaryValueBackendRuntype.nullable(),
    base_material_cost: MonetaryValueBackendRuntype.nullable(),
    total_scrap_cost: MonetaryValueBackendRuntype.nullable(),
    total_one_time_cost: MonetaryValueBackendRuntype.nullable(),
    total_offer_one_time_costs: MonetaryValueBackendRuntype.nullable(),
    total_manual_one_time_costs: MonetaryValueBackendRuntype.nullable(),
    /** TCO on the whole purchase quantity: it counts the TCO in `total_scrap_cost` and `total_excess_cost` again. */
    total_tco_cost: TcoCostItemRuntype.nullable(),
    total_excess_cost: MonetaryValueBackendRuntype.nullable(),
});
export type SolutionCostShareDTO = z.infer<typeof SolutionCostShareRuntype>;

export const PartDemandCostBreakdownRuntype = z.object({
    demand_id: z.string(),
    cost: SolutionCostShareRuntype,
});
export type PartDemandCostBreakdownDTO = z.infer<typeof PartDemandCostBreakdownRuntype>;

export const SourcingScenarioCalculationRuntype = z.object({
    sourcing_scenario_id: z.string(),
    line_item_calculations: z.array(z.custom<SourcingLineItemCalculationDTO>()),
    line_items_summary: LineItemsSummaryRuntype,
    excluded_bom_items: z.array(ExcludedBomItemRuntype),
    offer_selection_metadata: OfferSelectionMetadataRuntype,
    cost: SourcingScenarioCostRuntype,
    aggregated_top_level_assembly_information: z.record(z.string(), AssemblySourcingSummaryRuntype),
    ipn_to_cpns: z.record(z.string(), z.array(z.string())),
    design_items: z.record(z.string(), DesignItemInfoDtoRuntype),
    assemblies: z.record(z.string(), AssemblyInfoDtoRuntype),
    assembly_cost_breakdown: z.array(AssemblyCostBreakdownRuntype).default([]),
    part_cost_breakdown: z.array(PartDemandCostBreakdownRuntype).default([]),
});
export type SourcingScenarioCalculationDTO = z.infer<typeof SourcingScenarioCalculationRuntype>;

export const ExcludedBomItemsSummaryRuntype = z.object({
    do_not_place: z.number(),
    no_part_options: z.number(),
    quantity_error: z.number(),
});
export type ExcludedBomItemsSummaryDTO = z.infer<typeof ExcludedBomItemsSummaryRuntype>;

const PartySpendRuntype = z.object({
    id: z.string(),
    total_purchase_price: MonetaryValueBackendRuntype,
    line_count: z.number(),
    unpriced_line_count: z.number(),
});

const SpendDeltaRuntype = z.object({
    base: MonetaryValueBackendRuntype,
    scenario: MonetaryValueBackendRuntype,
    absolute: MonetaryValueBackendRuntype,
    relative: z.number().nullable(),
});

const BaseComparisonRuntype = z.object({
    base_sourcing_scenario_id: z.string(),
    sourced_in_both_line_count: z.number(),
    compared_line_count: z.number(),
    total_price: SpendDeltaRuntype,
    total_purchase_price: SpendDeltaRuntype,
});

export const SourcingScenarioCalculationSummaryRuntype = z.object({
    sourcing_scenario_id: z.string(),
    cost: SourcingScenarioCostRuntype,
    line_items_summary: LineItemsSummaryRuntype,
    offer_selection_metadata: OfferSelectionMetadataRuntype,
    excluded_bom_items_by_reason: ExcludedBomItemsSummaryRuntype,
    supplier_count: z.number(),
    manufacturer_count: z.number(),
    supplier_spend: z.array(PartySpendRuntype),
    manufacturer_spend: z.array(PartySpendRuntype),
    /** Total purchase price per manufacturer id, then per supplier id. */
    manufacturer_supplier_spend: z.record(z.string(), z.record(z.string(), MonetaryValueBackendRuntype)),
    unpriced_line_count: z.number(),
    comparison_to_base: BaseComparisonRuntype.nullable(),
});
export type SourcingScenarioCalculationSummaryDTO = z.infer<typeof SourcingScenarioCalculationSummaryRuntype>;

export const SourcingCalculationSummaryResponseRuntype = z.object({
    items: z.array(SourcingScenarioCalculationSummaryRuntype),
});

// Backward-compat aliases
export type SourcingCalculationDTO = SourcingScenarioCalculationDTO;
export const SourcingCalculationRuntype = SourcingScenarioCalculationRuntype;

export type SourcingCalculationResponseDTO = z.infer<typeof SourcingCalculationResponseRuntype>;
export const SourcingCalculationResponseRuntype: z.ZodType<{
    items: Array<SourcingScenarioCalculationDTO>;
}> = z.object({
    items: z.array(SourcingScenarioCalculationRuntype),
});

// Compare request (new format: baseline + array of references)
export const ComparisonInputRuntype = z.object({
    baseline: z.string(),
    references: z.array(z.string()),
});
export type ComparisonInputDTO = z.infer<typeof ComparisonInputRuntype>;

const DifferenceStatusRuntype = z.enum(['changed', 'unchanged', 'unknown']);
export type DifferenceStatus = z.infer<typeof DifferenceStatusRuntype>;

export const SimpleStatusDiffRuntype = z.object({
    status: DifferenceStatusRuntype,
});
export type SimpleStatusDiffDTO = z.infer<typeof SimpleStatusDiffRuntype>;

// Numeric diff
const NumericDiffWithValuesRuntype = z.object({
    status: z.union([z.literal('changed'), z.literal('unchanged')]),
    absolute: z.number(),
    relative: z.number(),
});
const NumericDiffUnknownRuntype = z.object({
    status: z.literal('unknown'),
    absolute: z.number().nullable(),
    relative: z.number().nullable(),
});
export const NumericDiffRuntype = z.discriminatedUnion('status', [
    NumericDiffWithValuesRuntype,
    NumericDiffUnknownRuntype,
]);

export type NumericDiffDTO = z.infer<typeof NumericDiffRuntype>;

const QuantityDiffWithValuesRuntype = z.object({
    status: z.union([z.literal('changed'), z.literal('unchanged')]),
    absolute: QuantityRuntype,
    relative: z.number(),
});
const QuantityDiffUnknownRuntype = z.object({
    status: z.literal('unknown'),
    absolute: QuantityRuntype.nullable(),
    relative: z.number().nullable(),
});
export const QuantityDiffRuntype = z.discriminatedUnion('status', [
    QuantityDiffWithValuesRuntype,
    QuantityDiffUnknownRuntype,
]);
export type QuantityDiffDTO = z.infer<typeof QuantityDiffRuntype>;

// Written out to avoid TS7056 in the endpoint definitions that use it.
export type MonetaryDiffDTO =
    | {
          status: 'changed' | 'unchanged';
          absolute: MonetaryValueBackend;
          relative: number;
      }
    | {
          status: 'unknown';
          absolute: MonetaryValueBackend | null;
          relative: number | null;
      };

const MonetaryDiffWithValuesRuntype = z.object({
    status: z.union([z.literal('changed'), z.literal('unchanged')]),
    absolute: MonetaryValueBackendRuntype,
    relative: z.number(),
});
const MonetaryDiffUnknownRuntype = z.object({
    status: z.literal('unknown'),
    absolute: MonetaryValueBackendRuntype.nullable(),
    relative: z.number().nullable(),
});
export const MonetaryDiffRuntype: z.ZodType<MonetaryDiffDTO> = z.discriminatedUnion('status', [
    MonetaryDiffWithValuesRuntype,
    MonetaryDiffUnknownRuntype,
]);

const SelectedPartDiffRuntype = z.enum([
    'MpnAndManufacturerChanged',
    'MpnChanged',
    'ManufacturerChanged',
    'PartChanged',
    'Unchanged',
    'Unknown',
]);

export type SelectedPartDiffDTO = z.infer<typeof SelectedPartDiffRuntype>;

const LineItemDifferencesRuntype = z.object({
    aggregated_quantity_diff: QuantityDiffRuntype,
    scrap_quantity_diff: QuantityDiffRuntype,
    required_quantity_diff: QuantityDiffRuntype,
    total_purchase_quantity_diff: QuantityDiffRuntype,

    mpq_diff: NumericDiffRuntype,
    moq_diff: NumericDiffRuntype,
    stock_diff: NumericDiffRuntype,
    factory_lead_time_diff: NumericDiffRuntype,
    cancellation_window_diff: NumericDiffRuntype,

    packaging_diff: SimpleStatusDiffRuntype,
    price_type_diff: SimpleStatusDiffRuntype,
    offer_origin_diff: SimpleStatusDiffRuntype,
    ncnr_diff: SimpleStatusDiffRuntype,
    unit_of_measurement_diff: SimpleStatusDiffRuntype,

    unit_price_diff: MonetaryDiffRuntype,
    total_price_diff: MonetaryDiffRuntype,
    total_purchase_price_diff: MonetaryDiffRuntype,
    excess_diff: MonetaryDiffRuntype,
    tco_cost_diff: MonetaryDiffRuntype,
    one_time_costs_diff: MonetaryDiffRuntype,
    scrap_price_diff: MonetaryDiffRuntype,

    availability_diff: z.unknown(),

    linked_location_diff: SimpleStatusDiffRuntype,
    selected_part_option_diff: SimpleStatusDiffRuntype,
    selected_part_diff: SelectedPartDiffRuntype,
    selection_diff: SimpleStatusDiffRuntype,

    sourced_by_diff: SimpleStatusDiffRuntype,
    status_diff: SimpleStatusDiffRuntype,
});
export type LineItemDifferencesDTO = z.infer<typeof LineItemDifferencesRuntype>;

// An interface, so TypeScript doesn't expand the type (TS7056).
export interface SummaryDiffDTO {
    total_price_diff: MonetaryDiffDTO;
    total_unit_price_diff: MonetaryDiffDTO;
    total_tco_costs_diff: MonetaryDiffDTO;
    total_one_time_costs_diff: MonetaryDiffDTO;
    total_excess_material_diff: MonetaryDiffDTO;
}
export const SummaryDiffRuntype: z.ZodType<SummaryDiffDTO> = z.object({
    total_price_diff: MonetaryDiffRuntype,
    total_unit_price_diff: MonetaryDiffRuntype,
    total_tco_costs_diff: MonetaryDiffRuntype,
    total_one_time_costs_diff: MonetaryDiffRuntype,
    total_excess_material_diff: MonetaryDiffRuntype,
});

// Backward-compat alias
export type SolutionConfigurationCalculationDTO = SourcingLineItemCalculationDTO;

// Baseline DTO — always present for each compared line item
export interface BaselineDTO {
    sourcing_scenario_id: string;
    line_item_calculation: SourcingLineItemCalculationDTO;
}

const BaselineRuntype: z.ZodType<BaselineDTO> = z.object({
    sourcing_scenario_id: z.string(),
    line_item_calculation: z.custom<SourcingLineItemCalculationDTO>(),
});

// Reference DTO — either a matched reference with differences, or a no-match
export type ReferenceDTO =
    | {
          type: 'Reference';
          sourcing_scenario_id: string;
          line_item_calculation: SourcingLineItemCalculationDTO;
          differences: LineItemDifferencesDTO;
      }
    | {
          type: 'NoMatch';
          sourcing_scenario_id: string;
      };

export const ReferenceRuntype: z.ZodType<ReferenceDTO> = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('Reference'),
        sourcing_scenario_id: z.string(),
        line_item_calculation: z.custom<SourcingLineItemCalculationDTO>(),
        differences: LineItemDifferencesRuntype,
    }),
    z.object({
        type: z.literal('NoMatch'),
        sourcing_scenario_id: z.string(),
    }),
]);

// ComparedLineItemDTO — one baseline + N references per line item
export interface ComparedLineItemDTO {
    baseline: BaselineDTO;
    references: ReferenceDTO[];
}

export const ComparedLineItemRuntype: z.ZodType<ComparedLineItemDTO> = z.object({
    baseline: BaselineRuntype,
    references: z.array(ReferenceRuntype),
});

// Comparison response (summaries + flat array of compared line items)
export interface ComparisonResultDTO {
    summaries: SummaryDiffDTO[];
    line_items: ComparedLineItemDTO[];
}
export const ComparisonResultRuntype: z.ZodType<ComparisonResultDTO> = z.object({
    summaries: z.array(SummaryDiffRuntype),
    line_items: z.array(ComparedLineItemRuntype),
});
